import { type ProblemDetails, problemDetailsSchema } from '@arabat/contracts';

/** An error response from the API, parsed from RFC 9457 problem details. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
  }

  get requestId(): string | undefined {
    return this.problem.requestId;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/** The API could not be reached (offline, DNS, CORS, server down). */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('The server could not be reached.', { cause });
    this.name = 'NetworkError';
  }
}

/** The API answered with a body that does not match the expected contract. */
export class ContractError extends Error {
  constructor(path: string, cause: unknown) {
    super(`Unexpected response shape from ${path}`, { cause });
    this.name = 'ContractError';
  }
}

/**
 * Our API always answers JSON. A successful status with an HTML page as the body comes from
 * something on the way — an office web filter that swaps the reply for its own page (seen at the
 * owner's office on 2026-11-04: `POST /auth/login` answered 200 text/html). Read it as NETWORK_BLOCKED
 * instead of failing later as an unexpected error.
 */
export function notJsonError(response: Response): ApiError | null {
  const type = response.headers.get('content-type') ?? '';
  // Only an HTML page counts: that is what a filter sends; our replies are never HTML.
  if (response.status === 204 || !/text\/html/i.test(type)) return null;
  return new ApiError({
    type: 'about:blank',
    title: 'Blocked on the way',
    status: response.status,
    code: 'NETWORK_BLOCKED',
  });
}

export async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }
  const parsed = problemDetailsSchema.safeParse(body);
  if (parsed.success) return new ApiError(parsed.data);
  return new ApiError({
    type: 'about:blank',
    title: response.statusText || 'Request failed',
    status: response.status,
    // The API answers every refusal with problem details: a bare 403 page comes from something
    // on the way, usually an office web filter (Blue Coat, FortiGate…; ADR-0159).
    code:
      response.status >= 500
        ? 'INTERNAL_ERROR'
        : response.status === 403
          ? 'NETWORK_BLOCKED'
          : 'MALFORMED_REQUEST',
  });
}
